import React, { useState } from 'react';
import { PlusIcon, SearchIcon, FilterIcon, CheckIcon, XIcon, EditIcon, DatabaseIcon, PackageIcon, ArchiveIcon, RefreshCwIcon, DownloadIcon, BuildingIcon, HomeIcon } from 'lucide-react';
import { Catalog, Vendor } from '../CatalogsManagement';
import { CatalogCard } from './CatalogCard';
import { OrgUnitSelector } from '../../settings/orgunit/OrgUnitSelector';
import { flattenOrgUnits, sampleOrgUnits } from '../../financial/sharedFinancialData';
interface CatalogListProps {
  catalogs: Catalog[];
  vendors: Vendor[];
  filters: {
    vendor: string;
    orgUnit: string;
    status: string;
  };
  onFilterChange: (name: string, value: string) => void;
  onCreateCatalog: () => void;
  onEditCatalog: (catalog: Catalog) => void;
  onManageSchema: (catalog: Catalog) => void;
  onManageProducts: (catalog: Catalog) => void;
  onToggleStatus: (catalogId: string) => void;
  locale: string;
}
export function CatalogList({
  catalogs,
  vendors,
  filters,
  onFilterChange,
  onCreateCatalog,
  onEditCatalog,
  onManageSchema,
  onManageProducts,
  onToggleStatus,
  locale
}: CatalogListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  // Get all OrgUnits for display
  const allOrgUnits = flattenOrgUnits(sampleOrgUnits);
  // Format date based on locale
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No End Date';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  // Get OrgUnit names for a catalog
  const getOrgUnitNames = (orgUnitIds: string[]) => {
    if (!orgUnitIds || orgUnitIds.length === 0) return 'All Organisation Units';
    return orgUnitIds.map(id => {
      const orgUnit = allOrgUnits.find(ou => ou.id === id);
      return orgUnit?.name || id;
    }).join(', ');
  };
  // Filter catalogs by search term
  const filteredCatalogs = catalogs.filter(catalog => catalog.name.toLowerCase().includes(searchTerm.toLowerCase()) || catalog.vendorName.toLowerCase().includes(searchTerm.toLowerCase()) || catalog.description.toLowerCase().includes(searchTerm.toLowerCase()));
  // Reset all filters
  const handleResetFilters = () => {
    onFilterChange('vendor', '');
    onFilterChange('orgUnit', '');
    onFilterChange('status', '');
    setSearchTerm('');
  };
  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  return <div className="space-y-6" data-id="element-12137">
      {/* Action bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0" data-id="element-12138">
        <div className="flex items-center space-x-2" data-id="element-12139">
          <div className="relative rounded-md shadow-sm" data-id="element-12140">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12141">
              <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-12142" />
            </div>
            <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search catalogs..." value={searchTerm} onChange={handleSearch} data-id="element-12143" />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12144">
            <FilterIcon className="h-4 w-4 mr-1" data-id="element-12145" />
            Filters
            {(filters.vendor || filters.orgUnit || filters.status) && <span className="ml-1 px-1.5 py-0.5 bg-indigo-100 text-indigo-800 rounded-full text-xs" data-id="element-12146">
                {[filters.vendor ? 1 : 0, filters.orgUnit ? 1 : 0, filters.status ? 1 : 0].reduce((a, b) => a + b, 0)}
              </span>}
          </button>
          <button onClick={handleResetFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12147">
            <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-12148" />
            Reset
          </button>
        </div>
        <div className="flex items-center space-x-2" data-id="element-12149">
          <div className="inline-flex shadow-sm rounded-md" data-id="element-12150">
            <button onClick={() => setViewMode('grid')} className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-l-md ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-gray-700 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-12151">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-12152">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" data-id="element-12153" />
              </svg>
            </button>
            <button onClick={() => setViewMode('table')} className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-r-md ${viewMode === 'table' ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-gray-700 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-12154">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-12155">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" data-id="element-12156" />
              </svg>
            </button>
          </div>
          <button onClick={onCreateCatalog} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12157">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-12158" />
            Add Catalog
          </button>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && <div className="bg-gray-50 p-4 rounded-md shadow-sm border border-gray-200" data-id="element-12159">
          <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-3" data-id="element-12160">
            <div data-id="element-12161">
              <label htmlFor="vendor-filter" className="block text-sm font-medium text-gray-700" data-id="element-12162">
                Vendor
              </label>
              <select id="vendor-filter" name="vendor-filter" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.vendor} onChange={e => onFilterChange('vendor', e.target.value)} data-id="element-12163">
                <option value="" data-id="element-12164">All Vendors</option>
                {vendors.map(vendor => <option key={vendor.id} value={vendor.id} data-id="element-12165">
                    {vendor.name}
                  </option>)}
              </select>
            </div>
            <div data-id="element-12166">
              <label htmlFor="orgUnit-filter" className="block text-sm font-medium text-gray-700" data-id="element-12167">
                Organisation Unit
              </label>
              <OrgUnitSelector value={filters.orgUnit} onChange={id => onFilterChange('orgUnit', id)} label="" placeholder="All Organisation Units" showPath={false} data-id="element-12168" />
            </div>
            <div data-id="element-12169">
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700" data-id="element-12170">
                Status
              </label>
              <select id="status-filter" name="status-filter" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.status} onChange={e => onFilterChange('status', e.target.value)} data-id="element-12171">
                <option value="" data-id="element-12172">All Statuses</option>
                <option value="Active" data-id="element-12173">Active</option>
                <option value="Inactive" data-id="element-12174">Inactive</option>
                <option value="Archived" data-id="element-12175">Archived</option>
              </select>
            </div>
          </div>
        </div>}

      {/* Catalogs grid view */}
      {viewMode === 'grid' && <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-12176">
          {filteredCatalogs.length === 0 ? <div className="col-span-full text-center py-12 bg-white rounded-lg shadow" data-id="element-12177">
              <div className="mx-auto h-12 w-12 text-gray-400 flex items-center justify-center" data-id="element-12178">
                <PackageIcon className="h-12 w-12" data-id="element-12179" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-12180">
                No catalogs found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-12181">
                Get started by creating a new catalog.
              </p>
              <div className="mt-6" data-id="element-12182">
                <button onClick={onCreateCatalog} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12183">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-12184" />
                  Add Catalog
                </button>
              </div>
            </div> : filteredCatalogs.map(catalog => <CatalogCard key={catalog.id} catalog={catalog} onEdit={() => onEditCatalog(catalog)} onManageSchema={() => onManageSchema(catalog)} onManageProducts={() => onManageProducts(catalog)} onToggleStatus={() => onToggleStatus(catalog.id)} locale={locale} data-id="element-12185" />)}
        </div>}

      {/* Catalogs table view */}
      {viewMode === 'table' && <div className="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-12186">
          {filteredCatalogs.length === 0 ? <div className="text-center py-12" data-id="element-12187">
              <div className="mx-auto h-12 w-12 text-gray-400 flex items-center justify-center" data-id="element-12188">
                <PackageIcon className="h-12 w-12" data-id="element-12189" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-12190">
                No catalogs found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-12191">
                Get started by creating a new catalog.
              </p>
              <div className="mt-6" data-id="element-12192">
                <button onClick={onCreateCatalog} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12193">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-12194" />
                  Add Catalog
                </button>
              </div>
            </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-12195">
              <thead className="bg-gray-50" data-id="element-12196">
                <tr data-id="element-12197">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12198">
                    Catalog
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12199">
                    Vendor
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12200">
                    Products
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12201">
                    Organisation Units
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12202">
                    Active Period
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12203">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12204">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-12205">
                {filteredCatalogs.map(catalog => <tr key={catalog.id} data-id="element-12206">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-12207">
                      <div className="text-sm font-medium text-indigo-600" data-id="element-12208">
                        {catalog.name}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-12209">
                        {catalog.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-12210">
                      <div className="text-sm text-gray-900" data-id="element-12211">
                        {catalog.vendorName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-12212">
                      <div className="text-sm text-gray-900" data-id="element-12213">
                        {catalog.productCount}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-12214">
                      <div className="text-sm text-gray-900" data-id="element-12215">
                        {catalog.orgUnitIds.length === 0 ? <span className="text-gray-500 italic" data-id="element-12216">
                            All Organisation Units
                          </span> : <div className="flex flex-wrap gap-1" data-id="element-12217">
                            {catalog.orgUnitIds.slice(0, 2).map(id => {
                    const orgUnit = allOrgUnits.find(ou => ou.id === id);
                    return <span key={id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-12218">
                                  {orgUnit?.name || id}
                                </span>;
                  })}
                            {catalog.orgUnitIds.length > 2 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-12219">
                                +{catalog.orgUnitIds.length - 2} more
                              </span>}
                          </div>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-12220">
                      <div className="text-sm text-gray-900" data-id="element-12221">
                        {formatDate(catalog.activeFrom)}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-12222">
                        to {formatDate(catalog.activeTo)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-12223">
                      {catalog.isActive ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-12224">
                          <CheckIcon className="h-4 w-4 mr-1" data-id="element-12225" />
                          Active
                        </span> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800" data-id="element-12226">
                          <XIcon className="h-4 w-4 mr-1" data-id="element-12227" />
                          Inactive
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-12228">
                      <div className="flex justify-end space-x-2" data-id="element-12229">
                        <button onClick={() => onEditCatalog(catalog)} className="text-indigo-600 hover:text-indigo-900" title="Edit Catalog" data-id="element-12230">
                          <EditIcon className="h-4 w-4" data-id="element-12231" />
                        </button>
                        <button onClick={() => onManageSchema(catalog)} className="text-indigo-600 hover:text-indigo-900" title="Manage Schema" data-id="element-12232">
                          <DatabaseIcon className="h-4 w-4" data-id="element-12233" />
                        </button>
                        <button onClick={() => onManageProducts(catalog)} className="text-indigo-600 hover:text-indigo-900" title="Manage Products" data-id="element-12234">
                          <PackageIcon className="h-4 w-4" data-id="element-12235" />
                        </button>
                        <button onClick={() => onToggleStatus(catalog.id)} className={catalog.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'} title={catalog.isActive ? 'Deactivate' : 'Activate'} data-id="element-12236">
                          {catalog.isActive ? <XIcon className="h-4 w-4" data-id="element-12237" /> : <CheckIcon className="h-4 w-4" data-id="element-12238" />}
                        </button>
                        <button className="text-gray-600 hover:text-gray-900" title="Export Products" data-id="element-12239">
                          <DownloadIcon className="h-4 w-4" data-id="element-12240" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>}
        </div>}
    </div>;
}